import { useMemo, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { fetchUserLikedMedia, insertLikedMedia, deleteLikedMedia } from "@/services/db/likedMedia";

interface LikedMediaItem {
  id: string;
  mediaUrl: string;
  mediaType: "image" | "video";
  thumbnailUrl?: string;
  referenceImageUrl?: string;
  prompt?: string;
  createdAt: string;
}

function toLikedMediaItem(item: Awaited<ReturnType<typeof fetchUserLikedMedia>>[number]): LikedMediaItem {
  return {
    id: item.id,
    mediaUrl: item.media_url,
    mediaType: item.media_type as "image" | "video",
    thumbnailUrl: item.thumbnail_url || undefined,
    referenceImageUrl: item.reference_image_url || undefined,
    prompt: item.prompt || undefined,
    createdAt: new Date(item.created_at).toLocaleDateString("he-IL"),
  };
}

export function useLikedMedia() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: likedMedia = [], isLoading } = useQuery({
    queryKey: ["liked", user?.id],
    queryFn: () => fetchUserLikedMedia(user!.id).then((data) => data.map(toLikedMediaItem)),
    enabled: !!user,
  });

  const likedUrls = useMemo(() => new Set(likedMedia.map((i) => i.mediaUrl)), [likedMedia]);

  const isLiked = useCallback(
    (mediaUrl: string) => likedUrls.has(mediaUrl),
    [likedUrls],
  );

  const mutation = useMutation({
    mutationFn: async ({
      mediaUrl,
      mediaType,
      options,
    }: {
      mediaUrl: string;
      mediaType: "image" | "video";
      options?: { thumbnailUrl?: string; referenceImageUrl?: string; prompt?: string };
    }) => {
      if (!user) return false;

      if (likedUrls.has(mediaUrl)) {
        await deleteLikedMedia(user.id, mediaUrl);
        return false;
      } else {
        await insertLikedMedia({
          user_id: user.id,
          media_url: mediaUrl,
          media_type: mediaType,
          thumbnail_url: options?.thumbnailUrl,
          reference_image_url: options?.referenceImageUrl,
          prompt: options?.prompt,
        });
        return true;
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["liked", user?.id] });
    },
  });

  const toggleLike = useCallback(
    async (
      mediaUrl: string,
      mediaType: "image" | "video",
      options?: { thumbnailUrl?: string; referenceImageUrl?: string; prompt?: string },
    ): Promise<boolean> => {
      try {
        return await mutation.mutateAsync({ mediaUrl, mediaType, options });
      } catch {
        return likedUrls.has(mediaUrl);
      }
    },
    [mutation, likedUrls],
  );

  return {
    likedMedia,
    loading: isLoading,
    isLiked,
    toggleLike,
    refetch: () => queryClient.invalidateQueries({ queryKey: ["liked", user?.id] }),
  };
}
